import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import './AISuggestionDrawer.scss';

// Re-use existing fetch util from panel to avoid code dup
import { AISuggestionPanel as _Dummy } from './AISuggestionPanel.jsx'; // only for typed import tree-shaking
// eslint-disable-next-line import/no-webpack-loader-syntax
// no-op : we just need fetchAISuggestions function

// We cannot import function directly; replicate minimal code here to avoid circular deps
const API_BASE_URL = process.env.REACT_APP_AI_API_BASE_URL || '/popin/api/1.0/ai';
const mapQuestionType = (qt) => (qt === 'default' ? 'open_ended' : qt);
const fetchAISuggestions = async (objective, questionType, userPrompt, user) => {
  // console.log('[AISuggestionDrawer] Fetching suggestions', { objective, questionType, userPrompt });
  // console.log('[AISuggestionDrawer] User from Redux:', user);
  
  const payload = { objective, question_type: mapQuestionType(questionType), prompt: userPrompt };
  // console.log('[AISuggestionDrawer] Request payload:', JSON.stringify(payload, null, 2));
  
  // Prepare headers with user context for tracking
  const headers = { 'Content-Type': 'application/json' };
  if (user && user.id && user.email) {
    headers['X-POPin-User-ID'] = String(user.id);
    headers['X-POPin-User-Email'] = user.email;
    // console.log('[AISuggestionDrawer] ✅ Adding user headers:', { userId: user.id, email: user.email });
  } else {
    console.warn('[AISuggestionDrawer] ⚠️ No user in Redux! Request will be anonymous');
  }
  // console.log('[AISuggestionDrawer] Final request headers:', headers);
  
  const response = await fetch(`${API_BASE_URL}/survey-generation/suggest-questions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({ detail: 'Unknown error' }));
    console.error('AI API error', errData);
    throw new Error(errData.detail || response.statusText);
  }
  const data = await response.json();
  // console.log('[AISuggestionDrawer] API response', data);
  return data.suggestions || [];
};

/**
 * AI-Powered Suggestion Drawer
 * Provides AI-generated question suggestions for survey creation
 */
const AISuggestionDrawer = ({
  isOpen,
  onClose,
  surveyObjective,
  questionType,
  onSelectSuggestion,
  initialPrompt = '',
}) => {
  const [prompt, setPrompt] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [userPrompt, setUserPrompt] = useState(initialPrompt);
  const textareaRef = useRef(null);
  
  // Get user from Redux store for tracking
  const user = useSelector((state) => state?.user?.user);
  // Auto-resize textarea function
  const autoResizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // Apply auto-resize when prompt changes
  useEffect(() => {
    if (isOpen) {
      setTimeout(autoResizeTextarea, 0);
    }
  }, [userPrompt, isOpen]);
  
  // Reset state when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset selection when drawer opens
      setSelectedIndex(null);
      // Set prompt if provided
      if (initialPrompt) {
        setUserPrompt(initialPrompt);
      }
    } else {
      // Clear selected state when drawer closes
      setSelectedIndex(null);
    }
  }, [isOpen, initialPrompt]);

  const drawerRef = useRef();

  // close on Esc key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // click outside drawer closes
  useEffect(() => {
    const handleClick = (e) => {
      if (isOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose]);

  const handleGenerate = async () => {
    // console.log('[AISuggestionDrawer] Generate button clicked!');
    // console.log('[AISuggestionDrawer] Current state:', {
    //   surveyObjective,
    //   questionType,
    //   userPrompt,
    //   isLoading,
    //   error
    // });
    
    if (!surveyObjective && !userPrompt) {
      // console.log('[AISuggestionDrawer] Validation failed: Both surveyObjective and userPrompt are empty');
      setError('Please enter a prompt or survey objective.');
      return;
    }
    
    // console.log('[AISuggestionDrawer] Validation passed, starting API call...');
    setIsLoading(true);
    setError('');
    
    try {
      // console.log('[AISuggestionDrawer] Calling fetchAISuggestions with params:', {
      //   surveyObjective,
      //   questionType,
      //   userPrompt,
      //   mappedQuestionType: mapQuestionType(questionType)
      // });
      
      const res = await fetchAISuggestions(surveyObjective, questionType, userPrompt, user);
      // console.log('[AISuggestionDrawer] API call successful, response:', res);
      setSuggestions(res);
    } catch (err) {
      console.error('[AISuggestionDrawer] API call failed:', err);
      console.error('[AISuggestionDrawer] Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError(err.message || 'Failed to fetch suggestions');
    } finally {
      // console.log('[AISuggestionDrawer] API call completed, setting loading to false');
      setIsLoading(false);
    }
  };

  const handleSelect = (suggestion) => {
    // console.log('handleSelect called with:', suggestion);
    // Only this suggestion should be selected now
    onSelectSuggestion(suggestion);
    
    // Clear selection after a moment (in case drawer is reopened)
    setTimeout(() => {
      setSelectedIndex(null);
    }, 300);
  };

  return (
    <div className={`ai-drawer-overlay ${isOpen ? 'open' : ''}`}> {/* backdrop */}
      <aside className={`ai-drawer ${isOpen ? 'open' : ''}`} ref={drawerRef}>
        <div className="ai-drawer__header">
          <h3 className="drawer-title">AI-Powered Suggestions</h3>
          <p className="ai-drawer__subtitle">
            Enter your POPin title and an example question, then click Generate.<br />
            Unsatisfied with the suggestions? Tweak the text and hit Generate again.
          </p>
          <div className="clickable" onClick={onClose}>×</div>
        </div>
        <section className="ai-drawer__prompt">
          <textarea
            ref={textareaRef}
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            onInput={autoResizeTextarea}
            placeholder="What kind of question would you like help with?"
          />
          <button className="btn btn-primary" onClick={() => handleGenerate()} disabled={isLoading}>
            {isLoading ? 'Generating…' : 'Generate'}
          </button>
        </section>
        {error && <div className="ai-drawer__error">{error}</div>}
        <section className="ai-drawer__suggestions">
          <h4>Suggested Questions</h4>
          {isLoading && <div className="ai-drawer__loading">Thinking…</div>}
          {!isLoading && suggestions.length > 0 && (
             <ul>
               {suggestions.slice(0,3).map((s, idx) => (
                 <li 
                  key={idx} 
                  className={selectedIndex === idx ? 'selected' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Set only this item as selected
                    setSelectedIndex(idx);
                    // Short delay to show selection animation
                    setTimeout(() => handleSelect(s), 200);
                  }}
                >
                  {s}
                </li>
               ))}
             </ul>
           )}
        </section>
      </aside>
    </div>
  );
};

export default AISuggestionDrawer;
